/**
 * Where the tap-card (the little colony/tile popover on the world map) goes.
 *
 * Preferred spot: its top-left corner just ABOVE the tap, to the right of it, so the card grows down and away from
 * the finger/cursor and the tapped tile stays visible beside it. If that spot would put any part of the card under
 * the map's own HUD (header patches, the minimap, Focus home, the chat pill, the bottom menu...) or off the map, the
 * nearest spot where it fits is used instead.
 *
 * Pure (rects in, position out) so it can be tested without a layout engine.
 */
export interface Box {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/** Gap between the card and the tap point, and the corner's lift above it. */
export const POPOVER_OFFSET_X = 14;
export const POPOVER_LIFT_Y = 28;
/** Breathing room kept between the card and every blocked box / the map edge. */
export const POPOVER_MARGIN = 6;
const SEARCH_STEP = 8;

function overlaps(a: Box, b: Box): boolean {
  return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
}

export function placePopover(opts: {
  /** The tap, in the map's own coordinates. */
  anchor: { x: number; y: number };
  size: { width: number; height: number };
  bounds: { width: number; height: number };
  blocked: Box[];
}): { left: number; top: number } {
  const { anchor, size, bounds, blocked } = opts;
  const maxLeft = Math.max(POPOVER_MARGIN, bounds.width - size.width - POPOVER_MARGIN);
  const maxTop = Math.max(POPOVER_MARGIN, bounds.height - size.height - POPOVER_MARGIN);
  const clamp = (left: number, top: number) => ({
    left: Math.min(maxLeft, Math.max(POPOVER_MARGIN, left)),
    top: Math.min(maxTop, Math.max(POPOVER_MARGIN, top)),
  });
  const grown = blocked.map((b) => ({
    left: b.left - POPOVER_MARGIN,
    top: b.top - POPOVER_MARGIN,
    right: b.right + POPOVER_MARGIN,
    bottom: b.bottom + POPOVER_MARGIN,
  }));
  const fits = (left: number, top: number) => {
    const box = { left, top, right: left + size.width, bottom: top + size.height };
    return !grown.some((b) => overlaps(box, b));
  };

  const preferred = clamp(anchor.x + POPOVER_OFFSET_X, anchor.y - POPOVER_LIFT_Y);
  if (fits(preferred.left, preferred.top)) return preferred;

  // Nearest free spot to the preferred one (by distance between top-left corners), scanning the whole map.
  let best: { left: number; top: number } | null = null;
  let bestDist = Infinity;
  for (let top = POPOVER_MARGIN; top <= maxTop; top += SEARCH_STEP) {
    for (let left = POPOVER_MARGIN; left <= maxLeft; left += SEARCH_STEP) {
      if (!fits(left, top)) continue;
      const dist = (left - preferred.left) ** 2 + (top - preferred.top) ** 2;
      if (dist < bestDist) {
        bestDist = dist;
        best = { left, top };
      }
    }
  }
  // Nowhere is clear (a tiny viewport): the card still has to open somewhere.
  return best ?? preferred;
}
